/**
 * Layered, DPR-aware canvases + a coalescing frame scheduler.
 *
 * Two layers, because the two things a chart repaints have wildly different
 * costs: moving the mouse redraws only the crosshair ("overlay"), while new
 * data redraws the marks ("plot"). Sharing one canvas would make every
 * mousemove re-stroke every series.
 *
 * DPR is applied once as a transform, so every drawing call afterwards is in
 * CSS pixels and nothing in the renderers has to know about retina. The OS's
 * UI-scale zoom is folded into that same transform — see {@link ChartLayers.measure}.
 */
import { effectiveUiZoom } from "../ui-scale";

export type LayerName = "plot" | "overlay";

export class ChartLayers {
  readonly el: HTMLElement;
  private canvases = new Map<LayerName, HTMLCanvasElement>();
  private contexts = new Map<LayerName, CanvasRenderingContext2D>();
  private dirty = new Set<LayerName>();
  private frame = 0;
  private ro: ResizeObserver | null = null;
  private disposed = false;

  /** LAYOUT pixels — NOT the zoomed rect. See {@link measure}. */
  width = 0;
  height = 0;
  dpr = 1;
  /** `document.body.style.zoom`. Hover has to divide by this too. */
  zoom = 1;

  /**
   * The ONE factor that maps layout px to the backing store. Every
   * `setTransform` on a layer must use this — `measure` used `dpr * zoom`
   * while `clear` restored `dpr` alone, so after any clear the context drew
   * `1/zoom` too large and the right and bottom edges fell off the canvas
   * silently. A line just ended off-screen and looked continuous; it took an
   * `endTag`, which has a visible right edge, to make it obvious.
   */
  get scale(): number {
    return this.dpr * this.zoom;
  }

  constructor(
    private onRender: (dirty: ReadonlySet<LayerName>) => void,
    private onResize: () => void
  ) {
    this.el = document.createElement("div");
    this.el.className = "os-chart-canvas";
    for (const name of ["plot", "overlay"] as LayerName[]) {
      const c = document.createElement("canvas");
      c.className = `os-chart-layer os-chart-layer--${name}`;
      // Pointer events land on the host element, never on a canvas; hit-testing
      // is done in chart-space, not by the DOM.
      c.style.pointerEvents = "none";
      this.el.appendChild(c);
      this.canvases.set(name, c);
      const g = c.getContext("2d");
      if (g) this.contexts.set(name, g);
    }
  }

  /**
   * The first measure MUST run onResize too. The chart builds its series before
   * it observes, so that first `layout()` runs against a 0x0 box and bails; if
   * the size we discover here only updated the canvases, the plot rect would
   * stay empty and every mark would draw at zero until something else happened
   * to resize the chart. That was the "blank until you click Table" bug.
   */
  observe(container: HTMLElement) {
    if (typeof ResizeObserver === "undefined") {
      this.sync();
      return;
    }
    this.ro = new ResizeObserver(() => this.sync());
    this.ro.observe(container);
    this.sync();
  }

  private sync() {
    if (!this.measure()) return;
    this.onResize();
    this.markAll();
  }

  context(name: LayerName): CanvasRenderingContext2D | null {
    return this.contexts.get(name) || null;
  }

  /**
   * Re-read the box, DPR and UI zoom. Returns true when anything changed.
   *
   * **UI SCALE IS `document.body.style.zoom`** (see `os/ui-scale.ts`), and the
   * two sides of this function speak different units:
   *
   *   - `getBoundingClientRect()` reports **zoomed** (visual) pixels.
   *   - `style.width` is a **layout** length, which zoom then scales AGAIN.
   *
   * Writing the rect straight back therefore shrinks the canvas by the zoom
   * factor on every scale below 1. At 0.85 the canvas rendered 848 -> 720.8
   * while its container stayed 848; since the layers are
   * `position:absolute;left:0`, that left a dead strip on the RIGHT where the
   * plot and gridlines simply stopped, with the chart's DOM header still
   * spanning the full width. Hover missed by the same factor, so the crosshair
   * trailed the cursor. It read as three unrelated bugs.
   *
   * So: `width`/`height` are LAYOUT px (what the chart draws in), the backing
   * store gets `dpr * zoom` device px to stay crisp, and the context transform
   * carries the same combined factor.
   */
  measure(): boolean {
    if (this.disposed) return false;
    const rect = this.el.getBoundingClientRect();
    const zoom = effectiveUiZoom();
    const w = Math.max(0, Math.round(rect.width / zoom));
    const h = Math.max(0, Math.round(rect.height / zoom));
    const dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
    if (w === this.width && h === this.height && dpr === this.dpr && zoom === this.zoom) return false;
    this.width = w;
    this.height = h;
    this.dpr = dpr;
    this.zoom = zoom;
    const scale = this.scale;
    for (const [name, c] of this.canvases) {
      c.width = Math.max(1, Math.round(w * scale));
      c.height = Math.max(1, Math.round(h * scale));
      c.style.width = w + "px";
      c.style.height = h + "px";
      const g = this.contexts.get(name);
      if (g) g.setTransform(scale, 0, 0, scale, 0, 0);
    }
    return true;
  }

  clear(name: LayerName) {
    const g = this.contexts.get(name);
    const c = this.canvases.get(name);
    if (!g || !c) return;
    // Reset the transform to clear device pixels, then restore it — cheaper
    // than save/restore and it guarantees the full bitmap is cleared.
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, c.width, c.height);
    g.setTransform(this.scale, 0, 0, this.scale, 0, 0);
  }

  mark(name: LayerName) {
    this.dirty.add(name);
    this.schedule();
  }

  markAll() {
    this.dirty.add("plot");
    this.dirty.add("overlay");
    this.schedule();
  }

  private schedule() {
    if (this.frame || this.disposed) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      if (this.disposed || this.dirty.size === 0) return;
      const dirty = new Set(this.dirty);
      this.dirty.clear();
      this.onRender(dirty);
    });
  }

  destroy() {
    this.disposed = true;
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.ro?.disconnect();
    this.ro = null;
    this.dirty.clear();
    this.canvases.clear();
    this.contexts.clear();
    this.el.remove();
  }
}

/** `Math.round(v) + 0.5` — the offset that makes a 1px stroke land on one pixel. */
export function crisp(v: number): number {
  return Math.round(v) + 0.5;
}

/** Rounded rectangle with per-corner radii, for the 4px rounded data-ends. */
export function roundedRect(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radii: [number, number, number, number]
) {
  const max = Math.min(Math.abs(w), Math.abs(h)) / 2;
  const [tl, tr, br, bl] = radii.map((r) => Math.max(0, Math.min(r, max))) as [
    number,
    number,
    number,
    number,
  ];
  g.beginPath();
  g.moveTo(x + tl, y);
  g.lineTo(x + w - tr, y);
  g.arcTo(x + w, y, x + w, y + tr, tr);
  g.lineTo(x + w, y + h - br);
  g.arcTo(x + w, y + h, x + w - br, y + h, br);
  g.lineTo(x + bl, y + h);
  g.arcTo(x, y + h, x, y + h - bl, bl);
  g.lineTo(x, y + tl);
  g.arcTo(x, y, x + tl, y, tl);
  g.closePath();
}
